import React, { useState, useEffect } from 'react';
import { getTransactionsApi, deleteTransactionApi } from '../../../Apis/Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faFileExport, faSearch } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import moment from 'moment';
import * as XLSX from 'xlsx';
import TransactionEditModal from './transaction_edit_modal';

const ViewTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    paymentMethod: '',
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Categories and payment methods
  const categories = [
    'food', 'transportation', 'utilities', 'supplies', 'rent',
    'maintenance', 'salaries', 'marketing', 'taxes', 'insurance',
    'professional_fees', 'other_expenses', 'sales', 'services',
    'interest', 'investments', 'other_income'
  ];

  const paymentMethods = [
    'cash', 'bank_transfer', 'esewa', 'fonepay', 'khalti',
    'credit_card', 'debit_card', 'other_digital'
  ];

  useEffect(() => {
    fetchTransactions();
  }, [filters, currentPage]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await getTransactionsApi({
        ...filters,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      });
      setTransactions(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await deleteTransactionApi(id);
        toast.success('Transaction deleted successfully');
        fetchTransactions();
      } catch (error) {
        toast.error('Failed to delete transaction');
      }
    }
  };

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const exportToExcel = () => {
  const incomeTransactions = transactions.filter(t => t.type === 'income');
  const expenseTransactions = transactions.filter(t => t.type === 'expense');

  const buildSection = (data, sectionTitle, startRow) => {
    const rows = [];
    const styles = {};

    // Section title
    rows.push([sectionTitle]);
    styles[`A${startRow + 1}`] = {
      fill: { fgColor: { rgb: 'FFF3B0' } },
      font: { bold: true },
    };

    // Merge across 6 columns for title
    const merges = [{ s: { r: startRow, c: 0 }, e: { r: startRow, c: 5 } }];

    // Header row
    const headerRow = ['Date', 'Category', 'Payment Method', 'Amount', 'Description', 'Remarks'];
    rows.push(headerRow);

    for (let c = 0; c < headerRow.length; c++) {
      styles[String.fromCharCode(65 + c) + (startRow + 2)] = {
        fill: { fgColor: { rgb: 'FFF3B0' } },
        font: { bold: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        },
      };
    }

    // Data rows
    const dataRows = data.map((t, i) => {
      const rowIndex = startRow + 3 + i;
      for (let c = 0; c < 6; c++) {
        const cellRef = String.fromCharCode(65 + c) + rowIndex;
        styles[cellRef] = {
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
          },
        };
      }
      return [
        moment(t.date).format('YYYY-MM-DD'),
        formatDisplayName(t.category),
        formatDisplayName(t.paymentMethod),
        t.amount,
        t.description,
        t.remarks || '-',
      ];
    });

    rows.push(...dataRows);

    // Total row
    const total = data.reduce((sum, t) => sum + t.amount, 0);
    rows.push(['', '', 'Total', total, '', '']);
    const totalRowIndex = startRow + 3 + dataRows.length;
    styles[`C${totalRowIndex + 1}`] = {
      font: { bold: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };
    styles[`D${totalRowIndex + 1}`] = {
      font: { bold: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    // Add an empty row after this table
    rows.push([]);

    return { rows, styles, merges };
  };

  const incomeStart = 0;
  const incomeTable = buildSection(transactions.filter(t => t.type === 'income'), 'Income', incomeStart);

  const expenseStart = incomeTable.rows.length;
  const expenseTable = buildSection(transactions.filter(t => t.type === 'expense'), 'Expenses', expenseStart);

  const allRows = [...incomeTable.rows, ...expenseTable.rows];
  const allStyles = { ...incomeTable.styles, ...expenseTable.styles };
  const allMerges = [...incomeTable.merges, ...expenseTable.merges.map(m => ({
    s: { r: m.s.r + expenseStart, c: m.s.c },
    e: { r: m.e.r + expenseStart, c: m.e.c },
  }))];

  // Build sheet
  const worksheet = XLSX.utils.aoa_to_sheet(allRows);

  // Apply styles
  Object.entries(allStyles).forEach(([cell, style]) => {
    if (!worksheet[cell]) worksheet[cell] = {};
    worksheet[cell].s = style;
  });

  // Apply merges
  worksheet['!merges'] = allMerges;

  // Sheet column width (optional)
  worksheet['!cols'] = [
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
    { wch: 12 },
    { wch: 30 },
    { wch: 20 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
  XLSX.writeFile(workbook, `transactions_${moment().format('YYYYMMDD')}.xlsx`);
};


  const formatDisplayName = (value) => {
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatAmount = (amount, type) => {
    return (
      <span className={type === 'income' ? 'text-green-600' : 'text-red-600'}>
        {type === 'income' ? '+' : '-'} NPR {amount.toFixed(2)}
      </span>
    );
  };

  return (
    <div className="mt-5">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h2 className="text-gray-600 font-bold text-2xl mb-4 md:mb-0">Transaction Records</h2>
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
            Export to Excel
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{formatDisplayName(cat)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                name="paymentMethod"
                value={filters.paymentMethod}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">All Methods</option>
                {paymentMethods.map(method => (
                  <option key={method} value={method}>{formatDisplayName(method)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md"
              />
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center w-full justify-center"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Search
              </button>
            </div>
          </form>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No transactions found matching your criteria
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Date</th>
                    <th className="py-3 px-6 text-left">Type</th>
                    <th className="py-3 px-6 text-left">Category</th>
                    <th className="py-3 px-6 text-right">Amount</th>
                    <th className="py-3 px-6 text-left">Payment Method</th>
                    <th className="py-3 px-6 text-left">Description</th>
                    <th className="py-3 px-6 text-left">Remarks</th>
                    <th className="py-3 px-6 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm">
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left whitespace-nowrap">
                        {moment(transaction.date).format('YYYY-MM-DD')}
                      </td>
                      <td className="py-3 px-6 text-left">
                        <span className={`px-2 py-1 rounded-full text-xs ${transaction.type === 'income'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {formatDisplayName(transaction.type)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-left">
                        {formatDisplayName(transaction.category)}
                      </td>
                      <td className="py-3 px-6 text-right">
                        {formatAmount(transaction.amount, transaction.type)}
                      </td>
                      <td className="py-3 px-6 text-left">
                        {formatDisplayName(transaction.paymentMethod)}
                      </td>
                      <td className="py-3 px-6 text-left">
                        {transaction.description}
                      </td>
                      <td className="py-3 px-6 text-left">
                        {transaction.remarks || '-'}
                      </td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDelete(transaction._id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, transactions.length)} of{' '}
                {transactions.length} entries
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={transactions.length < itemsPerPage}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <TransactionEditModal
          transaction={selectedTransaction}
          onClose={() => setEditModalOpen(false)}
          onUpdate={fetchTransactions}
        />
      )}
    </div>
  );
};

export default ViewTransactions;